import { CrudFilters } from "@pankod/refine-core";
import dayjs from "dayjs";

import { AuditDateFilterValue, AuditFilter } from "../types";

type FilledFilterValue = string | null | undefined;

const isFilledFilterValue = (value: FilledFilterValue): boolean =>
  typeof value === "string" && value.trim() !== "";

const addFilter = (
  filters: CrudFilters,
  field: string,
  value: FilledFilterValue,
): void => {
  if (!isFilledFilterValue(value)) {
    return;
  }

  filters.push({
    field,
    operator: "eq",
    value: value?.trim(),
  });
};

const toDayjs = (value?: AuditDateFilterValue | null) => {
  if (!value) {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    return value;
  }

  if (value instanceof Date || typeof value === "string") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === "object" && typeof value.format === "function") {
    const parsed = dayjs(value.format("YYYY-MM-DD"));
    return parsed.isValid() ? parsed : null;
  }

  return null;
};

export const normalizeAuditDateFrom = (
  value?: AuditDateFilterValue | null,
): string | null => {
  const parsed = toDayjs(value);

  return parsed ? parsed.startOf("day").toISOString() : null;
};

export const normalizeAuditDateTo = (
  value?: AuditDateFilterValue | null,
): string | null => {
  const parsed = toDayjs(value);

  return parsed ? parsed.endOf("day").toISOString() : null;
};

export const isInvalidAuditDateRange = (values: AuditFilter): boolean => {
  const from = toDayjs(values.date_from);
  const to = toDayjs(values.date_to);

  if (!from || !to) {
    return false;
  }

  return from.startOf("day").isAfter(to.startOf("day"));
};

export const buildAuditLogFilters = (values: AuditFilter): CrudFilters => {
  const filters: CrudFilters = [];

  if (isInvalidAuditDateRange(values)) {
    return filters;
  }

  addFilter(filters, "actor_id", values.actor);
  addFilter(filters, "resource", values.resource);
  addFilter(filters, "action", values.action);
  addFilter(filters, "entity_id", values.entity_id);
  addFilter(filters, "date_from", normalizeAuditDateFrom(values.date_from));
  addFilter(filters, "date_to", normalizeAuditDateTo(values.date_to));

  return filters;
};